import { IColumnPreferences } from './columnPreferences.types';

/**
 * localStorage-backed persistence for per-table column preferences.
 *
 * This is intentionally isolated from the hook/UI so it can be swapped
 * for a Brenda-backend Azure Function call later (e.g. via the existing
 * AbstractApiService pattern) without touching anything else in this feature.
 * The only requirement is that the replacement exposes the same three functions.
 */

const STORAGE_PREFIX = 'brenda:columnPreferences:';

const getStorageKey = (tableId: string): string => `${STORAGE_PREFIX}${tableId}`;

export const loadColumnPreferences = (tableId: string): IColumnPreferences | null => {
  try {
    const raw = window.localStorage.getItem(getStorageKey(tableId));

    if (!raw) {
      return null; }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed?.order) || !Array.isArray(parsed?.hidden)) {
      return null; }

    return parsed as IColumnPreferences;
  } catch {
    return null;
  }
};

export const saveColumnPreferences = (tableId: string, preferences: IColumnPreferences): void => {
  try {
    window.localStorage.setItem(getStorageKey(tableId), JSON.stringify(preferences));
  } catch {
    // Storage can fail silently (quota exceeded, private browsing, etc).
    // Column preferences are a nice-to-have, not critical path - fail quietly.
  }
};

export const clearColumnPreferences = (tableId: string): void => {
  try {
    window.localStorage.removeItem(getStorageKey(tableId));
  } catch {
    // no-op
  }
};

/**
 * Removes every persisted column-preference entry (all tables). Intended for
 * logout / session-expiry so one user's cached layout can't leak into the next
 * session on a shared browser, or override another user's role-based defaults.
 * The backend remains the source of truth and re-hydrates these on next login.
 */
export const clearAllColumnPreferences = (): void => {
  try {
    Object.keys(window.localStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .forEach(key => window.localStorage.removeItem(key));
  } catch {
    // no-op
  }
};